import React, { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { X, Printer, Barcode as BarcodeIcon, Settings2 } from 'lucide-react';
import { useReactToPrint } from 'react-to-print';
import { Asset } from '../types';
import { AssetLabel } from './AssetLabel';

interface AssetBulkLabelModalProps {
    assets: Asset[];
    onClose: () => void;
    companyName?: string;
    dateFormat?: string;
}

export const AssetBulkLabelModal: React.FC<AssetBulkLabelModalProps> = ({ assets, onClose, companyName, dateFormat = 'DD/MM/YYYY' }) => {
    const { t } = useTranslation('assets');
    const [activeTab, setActiveTab] = useState<'49x30' | '40x30' | '35x25'>('49x30');
    const componentRef = useRef<HTMLDivElement>(null);

    const handlePrint = useReactToPrint({
        contentRef: componentRef,
        documentTitle: `Bulk-Labels-${assets.length}-Assets`,
        pageStyle: `
            @page {
                size: auto;
                margin: 0mm;
            }
            @media print {
                body {
                    margin: 0;
                    display: flex;
                    justify-content: center;
                    align-items: center;
                    min-height: 100vh;
                }
            }
        `,
        onAfterPrint: () => console.log('Bulk Printed'),
    });

    return (
        <div className="fixed inset-0 bg-slate-900/50 backdrop-blur-sm z-[100] flex items-center justify-center p-4 animate-in fade-in duration-200">
            <div className="bg-[#F8FAFC] rounded-2xl w-full max-w-4xl shadow-2xl flex flex-col max-h-[90vh]">
                {/* Header */}
                <div className="bg-white p-4 rounded-t-2xl flex justify-between items-center border-b border-slate-100">
                    <div className="flex items-center gap-2 text-slate-800 font-bold text-lg">
                        <BarcodeIcon className="text-blue-500" />
                        <span>{t('labels.bulk_title', 'Μαζική Εκτύπωση')} ({assets.length})</span>
                    </div>
                    <button onClick={onClose} className="p-2 hover:bg-slate-50 rounded-full text-slate-400">
                        <X size={20} />
                    </button>
                </div>

                {/* Tabs */}
                <div className="p-6 pb-0 flex justify-center">
                    <div className="bg-white p-1 rounded-xl border border-slate-200 inline-flex shadow-sm">
                        <button
                            onClick={() => setActiveTab('49x30')}
                            className={`px-4 py-2 rounded-lg text-sm font-bold flex items-center gap-2 transition ${activeTab === '49x30' ? 'bg-white shadow text-blue-600 border border-slate-100' : 'text-slate-500 hover:text-slate-700'}`}
                        >
                            49x30mm
                        </button>
                        <button
                            onClick={() => setActiveTab('40x30')}
                            className={`px-4 py-2 rounded-lg text-sm font-bold flex items-center gap-2 transition ${activeTab === '40x30' ? 'bg-white shadow text-blue-600 border border-slate-100' : 'text-slate-500 hover:text-slate-700'}`}
                        >
                            40x30mm
                        </button>
                        <button
                            onClick={() => setActiveTab('35x25')}
                            className={`px-4 py-2 rounded-lg text-sm font-bold flex items-center gap-2 transition ${activeTab === '35x25' ? 'bg-white shadow text-blue-600 border border-slate-100' : 'text-slate-500 hover:text-slate-700'}`}
                        >
                            35x25mm
                        </button>
                    </div>
                </div>

                {/* Preview Area */}
                <div className="p-8 flex-1 overflow-y-auto bg-slate-100">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-8 justify-items-center">
                        {assets.map(asset => (
                            <div key={asset.id} className="w-full max-w-[400px]">
                                <AssetLabel asset={asset} activeTab={activeTab} companyName={companyName} dateFormat={dateFormat} />
                            </div>
                        ))}
                    </div>
                </div>

                {/* Hidden Print Container */}
                <div style={{ display: 'none' }}>
                    <div ref={componentRef}>
                        {assets.map((asset, index) => (
                            <div
                                key={asset.id}
                                style={{
                                    pageBreakAfter: 'always',
                                    height: '100vh',
                                    display: 'flex',
                                    justifyContent: 'center',
                                    alignItems: 'center'
                                }}
                            >
                                <div>
                                    <AssetLabel asset={asset} activeTab={activeTab} companyName={companyName} dateFormat={dateFormat} />
                                </div>
                            </div>
                        ))}
                    </div>
                </div>

                {/* Footer */}
                <div className="p-4 bg-white border-t border-slate-100 rounded-b-2xl flex justify-between items-center">
                    <button
                        onClick={onClose}
                        className="px-6 py-3 font-bold text-slate-500 hover:text-slate-800 transition"
                    >
                        {t('actions.close', 'Κλείσιμο')}
                    </button>
                    <button
                        onClick={handlePrint}
                        className="px-8 py-3 bg-[#0088CC] text-white rounded-xl font-bold hover:bg-[#0077B3] transition flex items-center gap-2 shadow-lg shadow-blue-100"
                    >
                        <Printer size={20} />
                        {t('actions.print_all', 'Εκτύπωση Όλων')}
                    </button>
                </div>
            </div>
        </div >
    );
};
